import { z } from 'zod';
import { EntityIdSchema, HhMmSchema, IsoDateSchema, IsoDateTimeSchema, PaginatedResponseSchema } from './common';

/**
 * Estados posibles de una sesion de clase.
 */
export const SesionEstadoSchema = z.enum(['planificada', 'en_curso', 'finalizada', 'cancelada']);
export type SesionEstado = z.infer<typeof SesionEstadoSchema>;

/**
 * Sesion (clase) de una cohorte.
 * Backend (DB + API) usa `hora_inicio_planificada` y `hora_fin_planificada`
 * como nombres de columna/JSON. Los nombres `hora_inicio_real` y
 * `hora_fin_real` son para los timestamps reales cuando se ejecuta la sesion.
 */
export const SesionSchema = z.object({
  id: EntityIdSchema,
  cohorte_id: EntityIdSchema,
  fecha: IsoDateSchema,
  hora_inicio_planificada: HhMmSchema,
  hora_fin_planificada: HhMmSchema,
  tema: z.string().nullable().optional(),
  unidad_curricular_id: EntityIdSchema.nullable().optional(),
  estado: SesionEstadoSchema,
  operador_inicio_id: EntityIdSchema.nullable().optional(),
  hora_inicio_real: IsoDateTimeSchema.nullable().optional(),
  hora_fin_real: IsoDateTimeSchema.nullable().optional(),
  operador_cierre_id: EntityIdSchema.nullable().optional(),
  created_at: IsoDateTimeSchema,
  // Datos de UC congelados al cerrar cohorte
  uc_nombre: z.string().nullable().optional(),
  uc_horas_modalidad_a: z.number().int().nullable().optional(),
  uc_horas_modalidad_b: z.number().int().nullable().optional(),
  uc_creditos: z.number().int().nullable().optional(),
});
export type Sesion = z.infer<typeof SesionSchema>;

/**
 * Input para POST /api/v1/sesiones.
 *
 * Refactor v2: admite `unidad_curricular_id` opcional para vincular la
 * sesión al módulo/materia del programa que se imparte.
 */
export const SesionCreateInputSchema = z.object({
  cohorte_id: EntityIdSchema,
  fecha: IsoDateSchema,
  hora_inicio: HhMmSchema,
  hora_fin: HhMmSchema,
  tema: z.string().min(1).optional(),
  unidad_curricular_id: EntityIdSchema.optional(),
});
export type SesionCreateInput = z.infer<typeof SesionCreateInputSchema>;

/**
 * Body opcional de POST /api/v1/sesiones/:id/cancelar.
 */
export const SesionCancelarInputSchema = z
  .object({ motivo: z.string().min(1).optional() })
  .optional();
export type SesionCancelarInput = z.infer<typeof SesionCancelarInputSchema>;

/**
 * Body opcional de DELETE /api/v1/sesiones/:id.
 *
 * El motivo queda en la copia archivada (`borrados.motivo`) y en la auditoría.
 */
export const SesionEliminarInputSchema = z
  .object({ motivo: z.string().min(1).optional() })
  .optional();
export type SesionEliminarInput = z.infer<typeof SesionEliminarInputSchema>;

/**
 * Patch para PATCH /api/v1/sesiones/:id.
 * Permite actualizar unidad_curricular_id y tema de una sesión ya creada.
 */
export const SesionPatchInputSchema = z.object({
  tema: z.string().min(1).optional(),
  unidad_curricular_id: EntityIdSchema.nullable().optional(),
});
export type SesionPatchInput = z.infer<typeof SesionPatchInputSchema>;

/**
 * Response de GET /api/v1/sesiones.
 */
export const SesionListResponseSchema = PaginatedResponseSchema(SesionSchema);
export type SesionListResponse = z.infer<typeof SesionListResponseSchema>;